import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Component, ElementRef, OnDestroy, ViewChild, effect, forwardRef, input, model, output, signal } from '@angular/core';
import { debounceTime, distinctUntilChanged, fromEvent, map, Subject, takeUntil, Subscription } from 'rxjs';

import { MultiSelectAutocompleteItem, MultiSelectAutocompleteItemSource } from './multi-select-autocomplete.model';

@Component({
  selector: 'pds-multi-select-autocomplete[source]',
  templateUrl: './multi-select-autocomplete.component.html',
  styleUrls: ['./multi-select-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectAutocompleteComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class MultiSelectAutocompleteComponent implements OnDestroy {
  @ViewChild('searchInput')
  searchElementRef!: ElementRef<HTMLInputElement>;

  private _sourceSignal = signal<MultiSelectAutocompleteItemSource | null>(null);
  private _historySignal = signal<MultiSelectAutocompleteItemSource | null>(null);
  private destroy$ = new Subject<void>();
  private inputChangeSubscription?: Subscription;

  source = input.required<MultiSelectAutocompleteItemSource>();
  history = input<MultiSelectAutocompleteItemSource>();
  placeholder = input<string>('');
  delay = input<number>(1000);
  noSearchMessage = input<string>('Utilize o campo de busca para encontrar resultados.');

  autocomplete = output<string>();

  showCombobox = false;
  search = '';
  disabled = false;

  values = model<MultiSelectAutocompleteItem[]>([]);
  onChange!: (value: MultiSelectAutocompleteItem[]) => void;
  onTouched!: () => void;

  get sourceData(): MultiSelectAutocompleteItemSource | null {
    return this._sourceSignal();
  }

  get historyData(): MultiSelectAutocompleteItemSource | null {
    return this._historySignal();
  }

  get searchInputElement() {
    return this.searchElementRef.nativeElement;
  }

  constructor() {
    effect(() => {
      this._sourceSignal.set(this.source());
      this._historySignal.set(this.history() || null);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.inputChangeSubscription?.unsubscribe();
  }

  writeValue(values: MultiSelectAutocompleteItem[]): void {
    this.values.set(Array.isArray(values) ? values : []);
  }

  registerOnChange(fn: (value: MultiSelectAutocompleteItem[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  setupOnChangeInputListener(inputElement: HTMLInputElement): void {
    if (this.inputChangeSubscription) this.inputChangeSubscription.unsubscribe();

    this.inputChangeSubscription = fromEvent<KeyboardEvent>(inputElement, 'keyup')
      .pipe(
        map((event) => (event.target as HTMLFormElement)['value'].trim()),
        distinctUntilChanged(),
        debounceTime(this.delay()),
        takeUntil(this.destroy$)
      )
      .subscribe(this.searchChanges.bind(this));
  }

  searchChanges(search: string): void {
    this.autocomplete.emit(search);
  }

  selectItem(item: MultiSelectAutocompleteItem): void {
    const currentValues = [...this.values()];
    const index = currentValues.findIndex((value) => value.id === item.id);

    if (index === -1) {
      item.selected = true;
      this.values.set([...currentValues, item]);
    } else {
      this.values.set(currentValues.filter((v) => v.id !== item.id));
    }

    this.onChange(this.values());
    this.resetSearch();
  }

  removeItem(item: MultiSelectAutocompleteItem, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    const filteredValues = this.values().filter((value) => value.id !== item.id);
    this.values.set(filteredValues);
    this.onChange(this.values());
  }

  isItemSelected(item: MultiSelectAutocompleteItem): boolean {
    return this.values().some((value) => value.id === item.id && value.selected);
  }

  handleSearchFocusEvent(): void {
    if (this.disabled) return;

    this.showCombobox = true;
    this.resetSearch();
    this.onTouched();
    this._sourceSignal.set({});

    setTimeout(() => {
      this.setupOnChangeInputListener(this.searchInputElement);
      this.focusSearchInput();
    });
  }

  handleOverlayClick(): void {
    this.showCombobox = false;
    this.resetSearch();
  }

  handleSearchKeydownEvent(event: KeyboardEvent): void {
    if (event.code === 'Tab' || event.code === 'Escape') {
      event.preventDefault();
      if (event.code === 'Escape') {
        this.handleOverlayClick();
      }
    }
  }

  private focusSearchInput(): void {
    if (this.searchElementRef?.nativeElement) {
      this.searchElementRef.nativeElement.focus();
    }
  }

  private resetSearch(): void {
    this.search = '';
    this.autocomplete.emit('');
  }
}
